<template>
  <div>
    <!-- État de chargement -->
    <b-card
      v-if="isLoading"
      class="p-3"
      aria-busy="true"
      aria-live="polite"
    >
      <div class="d-flex justify-content-center">
        <b-spinner
          variant="primary"
          label="Chargement..."
          aria-label="Chargement des informations du livreur"
        />
      </div>
    </b-card>

    <!-- État d'erreur -->
    <b-alert
      v-else-if="error"
      show
      variant="danger"
      aria-live="assertive"
      aria-label="Erreur lors du chargement des informations du livreur"
    >
      <h4 class="alert-heading">
        Erreur
      </h4>
      <div class="alert-body">
        {{ error }}
      </div>
    </b-alert>

    <!-- Contenu chargé avec succès -->
    <div v-else>
      <!-- Carte d'en-tête avec informations enrichies -->
      <b-card
        class="mb-3"
        aria-label="Résumé du livreur"
      >
        <b-row class="align-items-start">
          <b-col
            lg="8"
            md="7"
            sm="12"
          >
            <div class="d-flex align-items-center">
              <b-avatar
                :src="deliveryMan.avatar || null"
                :text="getInitials(deliveryMan.username)"
                size="4rem"
                variant="light-primary"
                class="mr-3"
                :aria-label="`Avatar du livreur ${deliveryMan.username}`"
              >
                <feather-icon
                  v-if="!deliveryMan.avatar"
                  icon="UserIcon"
                  size="28"
                  aria-hidden="true"
                />
              </b-avatar>

              <div>
                <h2
                  class="mb-1"
                  aria-label="Nom du livreur"
                >
                  {{ deliveryMan.username }}
                </h2>
                <div class="d-flex align-items-center mb-2">
                  <b-badge
                    :variant="deliveryMan.is_active ? 'success' : 'danger'"
                    pill
                    class="mr-2"
                  >
                    {{ deliveryMan.is_active ? 'Actif' : 'Inactif' }}
                  </b-badge>
                </div>

                <!-- Quick Info -->
                <div class="d-flex flex-wrap">
                  <div class="mr-3 mb-1">
                    <feather-icon
                      icon="PhoneIcon"
                      size="14"
                      class="text-primary mr-1"
                    />
                    <small>{{ deliveryMan.phone || 'N/A' }}</small>
                  </div>
                  <div class="mr-3 mb-1">
                    <feather-icon
                      icon="PhoneIcon"
                      size="14"
                      class="text-warning mr-1"
                    />
                    <small>{{ deliveryMan.phone2 || 'N/A' }}</small>
                  </div>
                  <div class="mr-3 mb-1">
                    <feather-icon
                      icon="MailIcon"
                      size="14"
                      class="text-primary mr-1"
                    />
                    <small>{{ deliveryMan.email || 'N/A' }}</small>
                  </div>
                  <div class="mr-3 mb-1">
                    <feather-icon
                      icon="HomeIcon"
                      size="14"
                      class="text-primary mr-1"
                    />
                    <small>{{ deliveryMan.repository_name || 'N/A' }}</small>
                  </div>
                </div>
              </div>
            </div>
          </b-col>

          <b-col
            lg="4"
            md="5"
            sm="12"
            class="text-md-right text-left mt-3 mt-md-0"
          >
            <!-- Action buttons -->
            <div class="action-buttons-container mb-2">
              <div class="action-buttons-row">
                <b-button
                  v-if="userIsAdmin"
                  :variant="deliveryMan.is_active ? 'outline-warning' : 'outline-success'"
                  size="sm"
                  class="action-btn toggle-btn mr-2 mb-2"
                  @click="toggleDeliveryManStatus"
                >
                  <feather-icon
                    :icon="deliveryMan.is_active ? 'UserXIcon' : 'UserCheckIcon'"
                    size="16"
                    class="mr-50"
                  />
                  <span>{{ deliveryMan.is_active ? 'Désactiver' : 'Activer' }}</span>
                </b-button>

                <b-button
                  v-if="userIsAdmin"
                  variant="outline-primary"
                  size="sm"
                  class="action-btn edit-btn mr-2 mb-2"
                  :to="{ name: 'delivery-man-edit', params: { id: deliveryMan.id }}"
                >
                  <feather-icon
                    icon="EditIcon"
                    size="16"
                    class="mr-50"
                  />
                  <span>Modifier</span>
                </b-button>

                <b-button
                  v-if="userIsAdmin"
                  variant="outline-secondary"
                  size="sm"
                  class="action-btn password-btn mr-2 mb-2"
                  :to="{ name: 'delivery-man-change-password', params: { id: deliveryMan.id }}"
                >
                  <feather-icon
                    icon="LockIcon"
                    size="16"
                    class="mr-50"
                  />
                  <span>Mot de passe</span>
                </b-button>

                <b-button
                  variant="outline-info"
                  size="sm"
                  class="action-btn back-btn mr-2 mb-2"
                  :to="{ name: 'delivery-man-list' }"
                >
                  <feather-icon
                    icon="ArrowLeftIcon"
                    size="16"
                    class="mr-50"
                  />
                  <span>Retour</span>
                </b-button>
              </div>
            </div>

            <!-- Stats Summary -->
            <div class="text-md-right text-left">
              <small class="text-muted">Membre depuis {{ formatDate(deliveryMan.created_at) }}</small>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <!-- Quick Stats Cards -->
      <b-row class="mb-3">
        <b-col
          lg="3"
          md="6"
          sm="12"
        >
          <b-card class="text-center stats-mini-card">
            <div class="avatar bg-light-success mx-auto mb-1">
              <feather-icon
                icon="PackageIcon"
                size="20"
              />
            </div>
            <h4 class="mb-0">
              {{ deliveryStats.totalDeliveries || 0 }}
            </h4>
            <small class="text-muted">Livraisons</small>
          </b-card>
        </b-col>

        <b-col
          lg="3"
          md="6"
          sm="12"
        >
          <b-card class="text-center stats-mini-card">
            <div class="avatar bg-light-info mx-auto mb-1">
              <feather-icon
                icon="CheckCircleIcon"
                size="20"
              />
            </div>
            <h4 class="mb-0">
              {{ deliveryStats.successRate || 0 }}%
            </h4>
            <small class="text-muted">Taux de réussite</small>
          </b-card>
        </b-col>

        <b-col
          lg="3"
          md="6"
          sm="12"
        >
          <b-card class="text-center stats-mini-card">
            <div class="avatar bg-light-warning mx-auto mb-1">
              <feather-icon
                icon="ClockIcon"
                size="20"
              />
            </div>
            <h4 class="mb-0">
              {{ deliveryStats.avgDeliveryTime || 0 }}h
            </h4>
            <small class="text-muted">Temps moyen</small>
          </b-card>
        </b-col>

        <b-col
          lg="3"
          md="6"
          sm="12"
        >
          <b-card class="text-center stats-mini-card">
            <div class="avatar bg-light-primary mx-auto mb-1">
              <feather-icon
                icon="StarIcon"
                size="20"
              />
            </div>
            <h4 class="mb-0">
              {{ deliveryStats.rating || 0 }}/5
            </h4>
            <small class="text-muted">Évaluation</small>
          </b-card>
        </b-col>
      </b-row>

      <b-card>
        <b-tabs nav-class="nav-tabs-modern">
          <b-tab active>
            <template #title>
              <feather-icon
                icon="UserIcon"
                size="16"
              />
              <span class="ml-1">Information</span>
            </template>

            <ShowInfo :delivery-man="deliveryMan" />
          </b-tab>

          <b-tab>
            <template #title>
              <feather-icon
                icon="TruckIcon"
                size="16"
              />
              <span class="ml-1">Livraisons</span>
            </template>

            <b-card-text>
              <div class="text-center py-4">
                <feather-icon
                  icon="TruckIcon"
                  size="48"
                  class="text-muted mb-2"
                />
                <h5 class="text-muted">
                  Historique des livraisons
                </h5>
                <p class="text-muted">
                  Cette section affichera l'historique des livraisons du livreur
                </p>
                <!-- TODO: Add deliveries component here -->
              </div>
            </b-card-text>
          </b-tab>

          <b-tab>
            <template #title>
              <feather-icon
                icon="DollarSignIcon"
                size="16"
              />
              <span class="ml-1">Paiements</span>
            </template>

            <b-card-text>
              <div class="text-center py-4">
                <feather-icon
                  icon="DollarSignIcon"
                  size="48"
                  class="text-muted mb-2"
                />
                <h5 class="text-muted">
                  Historique des paiements
                </h5>
                <p class="text-muted">
                  Cette section affichera l'historique des paiements du livreur
                </p>
                <!-- TODO: Add payments component here -->
              </div>
            </b-card-text>
          </b-tab>

          <b-tab>
            <template #title>
              <feather-icon
                icon="BarChart3Icon"
                size="16"
              />
              <span class="ml-1">Statistiques</span>
            </template>

            <b-card-text>
              <div class="text-center py-4">
                <feather-icon
                  icon="BarChart3Icon"
                  size="48"
                  class="text-muted mb-2"
                />
                <h5 class="text-muted">
                  Statistiques détaillées
                </h5>
                <p class="text-muted">
                  Cette section affichera des graphiques et statistiques détaillées
                </p>
                <!-- TODO: Add charts and detailed statistics here -->
              </div>
            </b-card-text>
          </b-tab>
        </b-tabs>
      </b-card>
    </div>

    <!-- Status toggle confirmation modal -->
    <b-modal
      ref="statusModal"
      :title="deliveryMan.is_active ? 'Désactiver le livreur' : 'Activer le livreur'"
      @ok="handleStatusConfirmed"
    >
      <p>Êtes-vous sûr de vouloir {{ deliveryMan.is_active ? 'désactiver' : 'activer' }} ce livreur?</p>
    </b-modal>
  </div>
</template>

<script>
import {
  BTabs, BTab, BCardText, BCard, BButton, BSpinner,
  BAlert, BModal, BRow, BCol, BBadge, BAvatar,
} from 'bootstrap-vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import storeAuth from '@/store/store'
import ShowInfo from './ShowInfo.vue'

export default {
  components: {
    BTabs,
    BTab,
    BCardText,
    BCard,
    BButton,
    BSpinner,
    BAlert,
    BModal,
    BRow,
    BCol,
    BBadge,
    BAvatar,
    ShowInfo,
  },

  data() {
    return {
      deliveryMan: {},
      isLoading: true,
      error: null,
      isSubmittingPassword: false,
      passwordForm: {
        newPassword: '',
      },
      deliveryStats: {
        totalDeliveries: 0,
        successRate: 0,
        avgDeliveryTime: 0,
        rating: 0,
      },
    }
  },

  computed: {
    storeAuth() {
      return storeAuth
    },

    userIsAdmin() {
      const { user } = storeAuth.state
      if (!user) return false

      // Check if user has admin role (multiple ways it could be stored)
      return user.role === 'admin'
             || user.role === 'Admin'
             || (user.groups && user.groups.some(group => group.name === 'admin' || group.name === 'Admin'))
             || (user.groups && Array.isArray(user.groups) && user.groups.includes('admin'))
             || user.is_admin === true
             || user.is_superuser === true
    },
  },

  created() {
    this.fetchDeliveryManDetails()
  },

  methods: {
    async fetchDeliveryManDetails() {
      this.isLoading = true
      try {
        // Use the new dedicated endpoint
        const response = await axios.get(`/api/auth/users/delivery-men/${this.$route.params.id}/`)
        this.deliveryMan = response.data

        // Load delivery statistics (mock data for now)
        this.loadDeliveryStats()
      } catch (error) {
        this.error = error.response?.data?.message || 'Une erreur est survenue lors du chargement'
      } finally {
        this.isLoading = false
      }
    },

    loadDeliveryStats() {
      // TODO: Replace with actual API call
      // For now, we'll use mock data based on user ID to simulate different stats
      const userId = this.deliveryMan.id || 1
      this.deliveryStats = {
        totalDeliveries: Math.floor(Math.random() * 100) + 20 + (userId % 10),
        successRate: Math.floor(Math.random() * 20) + 80, // 80-100%
        avgDeliveryTime: (Math.random() * 3 + 1).toFixed(1), // 1-4 hours
        rating: (Math.random() * 1.5 + 3.5).toFixed(1), // 3.5-5.0
      }
    },

    getInitials(name) {
      if (!name) return '??'
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
        .substring(0, 2)
    },

    formatDate(dateString) {
      return format(new Date(dateString), 'dd MMMM yyyy, HH:mm', { locale: fr })
    },

    toggleDeliveryManStatus() {
      this.$refs.statusModal.show()
    },

    async handleStatusConfirmed() {
      try {
        // Use the new dedicated endpoint for toggling activation
        const response = await axios.post(`/api/auth/users/delivery-men/toggle-activation/${this.deliveryMan.id}/`)
        this.deliveryMan = response.data
        this.showToast('success', `Le livreur a été ${this.deliveryMan.is_active ? 'activé' : 'désactivé'} avec succès`)
      } catch (error) {
        this.showToast('danger', error.response?.data?.error || 'Erreur lors du changement de statut')
      }
    },

    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: variant === 'success' ? 'Succès' : 'Erreur',
          icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon',
          variant,
          text,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.info-section {
  margin-bottom: 1.5rem;
}

.section-title {
  margin-bottom: 1rem;
  font-weight: 500;
  color: #5e5873;
  font-size: 1.1rem;
}

.delivery-man-info-container {
  background-color: #f8f8f8;
  border-radius: 0.5rem;
  padding: 1.5rem;
}

/* Enhanced Action Buttons Styling */
.action-buttons-container {
  .action-buttons-row {
    gap: 0.5rem;
    flex-wrap: wrap;

    .action-btn:not(:last-child) {
      margin-right: 0.5rem;
      margin-bottom: 0.5rem;
    }

    .action-btn:last-child {
      margin-bottom: 0.5rem;
    }
  }

  .action-btn {
    min-width: auto;
    padding: 0.375rem 0.75rem;
    border-radius: 0.375rem;
    font-weight: 500;
    font-size: 0.875rem;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    border-width: 1px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.15), transparent);
      transition: left 0.4s;
    }

    &:hover::before {
      left: 100%;
    }

    .btn-icon {
      transition: transform 0.2s ease;
      margin-right: 0.375rem;
    }

    .btn-text {
      font-size: 0.875rem;
      font-weight: 500;
    }

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

      .btn-icon {
        transform: scale(1.1);
      }
    }

    &.toggle-btn {
      &.btn-outline-success {
        border-color: #28c76f;
        color: #28c76f;

        &:hover {
          background: #28c76f;
          border-color: #28c76f;
          color: white;
          box-shadow: 0 6px 20px rgba(40, 199, 111, 0.3);
        }
      }

      &.btn-outline-warning {
        border-color: #ff9f43;
        color: #ff9f43;

        &:hover {
          background: #ff9f43;
          border-color: #ff9f43;
          color: white;
          box-shadow: 0 6px 20px rgba(255, 159, 67, 0.3);
        }
      }
    }

    &.edit-btn {
      border-color: #E80000;
      color: #E80000;

      &:hover {
        background: linear-gradient(45deg, #E80000, #ff4d4d);
        border-color: #E80000;
        color: white;
        box-shadow: 0 6px 20px rgba(232, 0, 0, 0.3);
      }
    }

    &.password-btn {
      border-color: #1D1D1B;
      color: #1D1D1B;

      &:hover {
        background: #1D1D1B;
        border-color: #1D1D1B;
        color: white;
        box-shadow: 0 6px 20px rgba(29, 29, 27, 0.3);
      }
    }

    &.back-btn {
      border-color: #00cfe8;
      color: #00cfe8;

      &:hover {
        background: #00cfe8;
        border-color: #00cfe8;
        color: white;
        box-shadow: 0 6px 20px rgba(0, 207, 232, 0.3);
      }
    }
  }
}

/* Enhanced Stats Cards for Show Page */
.stats-mini-card {
  border: 1px solid #e9ecef;
  border-radius: 0.75rem;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #E80000, #ff4d4d);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(232, 0, 0, 0.1);
    border-color: #E80000;

    &::before {
      opacity: 1;
    }

    .avatar {
      transform: scale(1.1);
    }
  }

  .avatar {
    width: 3rem;
    height: 3rem;
    border-radius: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

    &.bg-light-success {
      background: linear-gradient(45deg, rgba(40, 199, 111, 0.1), rgba(40, 199, 111, 0.2));
      color: #28c76f;
    }

    &.bg-light-info {
      background: linear-gradient(45deg, rgba(0, 207, 232, 0.1), rgba(0, 207, 232, 0.2));
      color: #00cfe8;
    }

    &.bg-light-warning {
      background: linear-gradient(45deg, rgba(255, 159, 67, 0.1), rgba(255, 159, 67, 0.2));
      color: #ff9f43;
    }

    &.bg-light-primary {
      background: linear-gradient(45deg, rgba(232, 0, 0, 0.1), rgba(232, 0, 0, 0.2));
      color: #E80000;
    }
  }

  h4 {
    font-weight: 700;
    color: #1D1D1B;
    margin-bottom: 0.25rem;
  }

  small {
    color: #6c757d;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

/* Enhanced Header Card */
.card {
  border-radius: 0.75rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;
}

/* Modern Tab Styling */
.nav-tabs-modern {
  border-bottom: 2px solid #e9ecef;

  .nav-link {
    border: none;
    border-radius: 0;
    color: #6c757d;
    font-weight: 500;
    padding: 1rem 1.5rem;
    transition: all 0.3s ease;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      right: 0;
      height: 2px;
      background: #E80000;
      transform: scaleX(0);
      transition: transform 0.3s ease;
    }

    &:hover {
      color: #E80000;
      background: rgba(232, 0, 0, 0.05);
    }

    &.active {
      color: #E80000;
      background: rgba(232, 0, 0, 0.05);

      &::after {
        transform: scaleX(1);
      }
    }
  }
}

/* Responsive Improvements for Show Page */
@media (max-width: 768px) {
  .action-buttons-container {
    .action-buttons-row {
      gap: 0.25rem;

      .action-btn:not(:last-child) {
        margin-right: 0.25rem;
      }
    }

    .action-btn {
      min-width: auto;
      flex: 1;
      margin-bottom: 0.5rem;

      .btn-text {
        display: none;
      }

      .btn-icon {
        margin-right: 0;
      }
    }
  }

  .stats-mini-card {
    margin-bottom: 1rem;

    .avatar {
      width: 2.5rem;
      height: 2.5rem;
    }

    h4 {
      font-size: 1.25rem;
    }
  }
}

@media (max-width: 576px) {
  .action-buttons-container {
    .action-buttons-row {
      flex-direction: column;

      .action-btn:not(:last-child) {
        margin-right: 0;
      }
    }

    .action-btn {
      width: 100%;
      justify-content: center;
      margin-bottom: 0.5rem;

      .btn-text {
        display: inline;
      }

      .btn-icon {
        margin-right: 0.5rem;
      }
    }
  }
}
</style>
